import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Chip, Select, SelectItem, Slider, Spinner } from "@heroui/react";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import FundPickerBar from "../components/FundPickerBar";
import { api } from "../services/api";
import {
  AuditEmptyState,
  ChangeList,
  EmptyFlagList,
  SourceDiffList,
} from "../components/AuditResultList";
import {
  compareNumericField,
  diffCrossSource,
  diffFillState,
  diffPeriodOverPeriod,
  flagEmpty,
  groupByPeriod,
  sortDocIdsByPeriodDesc,
} from "../model/auditUtils";
import {
  FO_AUDIT_FIELDS,
  PORTFOLIO_CRITICAL_FIELDS,
  PORTFOLIO_GENINFO_FIELDS,
  PORTFOLIO_NUMERIC_FIELDS,
} from "../model/auditFields";
import { getVal, periodKey, sortPeriods } from "../model/utils";
import type { PeRow } from "../model/utils";
import type { DocMeta } from "../model/types";

type ViewKind = "fund_overview" | "portfolio" | "capital_account";

const VIEW_OPTIONS: { key: ViewKind; label: string }[] = [
  { key: "fund_overview", label: "Fund Overview" },
  { key: "portfolio", label: "Portfolio" },
  { key: "capital_account", label: "Capital Account" },
];

function getViewDocIds(
  docIds: string[],
  view: ViewKind,
  docMeta: Record<string, DocMeta>,
): string[] {
  const withFlag = (() => {
    if (view === "fund_overview") {
      return docIds.filter((docId) => docMeta[docId]?.has_fund_overview);
    }
    if (view === "portfolio") {
      return docIds.filter((docId) => docMeta[docId]?.has_portfolio_review);
    }
    return docIds.filter((docId) => docMeta[docId]?.has_ca);
  })();

  return withFlag.length > 0 ? withFlag : docIds;
}

export default function AuditTabView() {
  const { funds, docMeta, selectedFundId } = useS2DashboardContext();

  const [view, setView] = useState<ViewKind>("fund_overview");
  const [period, setPeriod] = useState<string | null>(null);
  const [threshold, setThreshold] = useState<number>(0.2); // 20%

  const selectedFund = funds.find((f) => f.fund_id === selectedFundId) ?? null;
  const viewDocIds = useMemo(
    () =>
      selectedFund ? getViewDocIds(selectedFund.doc_ids, view, docMeta) : [],
    [selectedFund, view, docMeta],
  );

  // ── Period options ────────────────────────────────────────────────────────
  const periodOptions = useMemo(() => {
    if (!selectedFund) return [];
    const unique = new Set<string>();
    for (const docId of viewDocIds) {
      const p = docMeta[docId]?.reporting_period;
      if (p) unique.add(p);
    }
    return sortPeriods(Array.from(unique)).reverse();
  }, [selectedFund, viewDocIds, docMeta]);

  // Auto-select most recent period
  useEffect(() => {
    if (!selectedFund) {
      setPeriod(null);
      return;
    }
    if (!period || !periodOptions.includes(period)) {
      setPeriod(periodOptions[0] ?? null);
    }
  }, [selectedFund, periodOptions, period]);

  // Previous period (for period-over-period comparison)
  const previousPeriod = useMemo(() => {
    if (!period) return null;
    const idx = periodOptions.indexOf(period);
    return idx >= 0 && idx < periodOptions.length - 1
      ? periodOptions[idx + 1]
      : null;
  }, [period, periodOptions]);

  return (
    <div className="flex flex-col gap-4">
      <FundPickerBar mode="single" />

      {!selectedFundId ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : (
        <>
          {/* Controls bar */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-2 flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                Audit
              </span>
              <span className="text-[11px] text-gray-400">
                · detect what changed between periods and flag outliers
              </span>
            </div>
            <div className="flex items-end gap-4 flex-wrap px-4 py-3">
              <div className="w-44">
                <Select
                  label="View"
                  size="sm"
                  selectedKeys={new Set([view])}
                  onSelectionChange={(keys) => {
                    const v = Array.from(keys)[0] as ViewKind;
                    if (v) setView(v);
                  }}
                  aria-label="Audit view"
                >
                  {VIEW_OPTIONS.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>
              </div>

              <div className="w-40">
                <Select
                  label="Period"
                  size="sm"
                  selectedKeys={period ? new Set([period]) : new Set()}
                  onSelectionChange={(keys) => {
                    const p = Array.from(keys)[0] as string;
                    setPeriod(p ?? null);
                  }}
                  aria-label="Period"
                >
                  {periodOptions.map((p) => (
                    <SelectItem key={p}>{p}</SelectItem>
                  ))}
                </Select>
              </div>

              {view !== "fund_overview" && (
                <div className="flex w-72 flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      Threshold
                    </label>
                    <Chip
                      size="sm"
                      variant="flat"
                      color="primary"
                      classNames={{
                        base: "h-6 border border-blue-100 bg-blue-50",
                        content:
                          "px-0 text-[11px] font-semibold tabular-nums text-[#001d6c]",
                      }}
                    >
                      {(threshold * 100).toFixed(0)}%
                    </Chip>
                  </div>
                  <Slider
                    aria-label="Threshold percentage"
                    size="sm"
                    color="primary"
                    minValue={5}
                    maxValue={100}
                    step={5}
                    value={Math.round(threshold * 100)}
                    onChange={(value) => {
                      const next = Array.isArray(value) ? value[0] : value;
                      setThreshold(next / 100);
                    }}
                    showSteps
                    classNames={{
                      base: "max-w-none",
                      labelWrapper: "hidden",
                      filler: "bg-[#001d6c]",
                      thumb:
                        "before:hidden after:bg-white after:border after:border-[#001d6c]",
                      track: "border border-gray-200 bg-gray-100",
                      step: "data-[in-range=true]:bg-[#001d6c]",
                    }}
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 px-0.5">
                    <span>5%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}

              {previousPeriod && (
                <div className="ml-auto text-[11px] text-gray-500">
                  Comparing{" "}
                  <span className="font-semibold text-gray-700">
                    {previousPeriod}
                  </span>
                  {" → "}
                  <span className="font-semibold text-gray-700">{period}</span>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {viewDocIds.length === 0 ? (
            <AuditEmptyState
              message={`No ${
                VIEW_OPTIONS.find((option) => option.key === view)?.label ??
                "audit"
              } document is available for this fund.`}
            />
          ) : period && selectedFundId ? (
            <>
              {view === "fund_overview" && (
                <FundOverviewAudit
                  fundId={selectedFundId}
                  period={period}
                  previousPeriod={previousPeriod}
                  docMeta={docMeta}
                  docIds={viewDocIds}
                />
              )}
              {view === "portfolio" && (
                <PortfolioAudit
                  fundId={selectedFundId}
                  period={period}
                  previousPeriod={previousPeriod}
                  docMeta={docMeta}
                  docIds={viewDocIds}
                  threshold={threshold}
                />
              )}
              {view === "capital_account" && (
                <CapitalAccountAudit
                  fundId={selectedFundId}
                  period={period}
                  previousPeriod={previousPeriod}
                  docMeta={docMeta}
                  docIds={viewDocIds}
                  threshold={threshold}
                />
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

// ─── Fund Overview Audit ─────────────────────────────────────────────────────

interface AuditProps {
  fundId: string;
  period: string;
  previousPeriod: string | null;
  docMeta: Record<string, DocMeta>;
  docIds: string[];
}

function getPortfolioCompanyName(row: PeRow): string {
  return (
    getVal(
      row,
      "portfolio_review_general_information_company_name_long_name",
    ) ||
    getVal(
      row,
      "portfolio_review_general_information_company_name_short_name",
    ) ||
    ""
  );
}

function getPortfolioInvId(row: PeRow): string {
  return (
    getVal(row, "portfolio_review_system_identifiers_inv_id") ||
    getVal(row, "investments_summary_inv_id") ||
    ""
  );
}

function pickDocForPeriod(
  docIds: string[],
  period: string,
  docMeta: Record<string, DocMeta>,
  requireFO = false,
): string | null {
  const k = periodKey(period);
  // Prefer doc with has_fund_overview when requested
  const candidates = docIds.filter(
    (d) =>
      docMeta[d]?.reporting_period &&
      periodKey(docMeta[d]!.reporting_period) === k,
  );
  if (requireFO) {
    const withFO = candidates.find((d) => docMeta[d]?.has_fund_overview);
    if (withFO) return withFO;
  }
  return candidates[0] ?? null;
}

function FundOverviewAudit({
  fundId,
  period,
  previousPeriod,
  docMeta,
  docIds,
}: AuditProps) {
  const curDoc = pickDocForPeriod(docIds, period, docMeta, true);
  const prevDoc = previousPeriod
    ? pickDocForPeriod(docIds, previousPeriod, docMeta, true)
    : null;

  const queries = useQueries({
    queries: [
      {
        queryKey: ["audit", "fo", fundId, curDoc],
        queryFn: () => api.getFundOverview(fundId, curDoc!),
        enabled: !!curDoc,
        staleTime: 30_000,
      },
      {
        queryKey: ["audit", "fo", fundId, prevDoc],
        queryFn: () => api.getFundOverview(fundId, prevDoc!),
        enabled: !!prevDoc,
        staleTime: 30_000,
      },
    ],
  });

  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.find((q) => q.isError)?.error as Error | undefined;
  const after = queries[0].data?.[0] ?? null;
  const before = queries[1].data?.[0] ?? null;

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label="Analysing…" color="primary" />
      </div>
    );
  }

  if (!curDoc) {
    return (
      <AuditEmptyState message="No Fund Overview document available for this period." />
    );
  }

  if (error) {
    return (
      <AuditEmptyState
        message={`Could not load Fund Overview audit data: ${error.message}`}
      />
    );
  }

  if (!prevDoc) {
    return (
      <AuditEmptyState
        message={`No previous period to compare against. This is the earliest Fund Overview (${period}).`}
      />
    );
  }

  const changes = diffFillState(
    before as PeRow | null,
    after as PeRow | null,
    FO_AUDIT_FIELDS,
  );

  if (changes.length === 0) {
    return (
      <AuditEmptyState message="No changes detected between these two periods." />
    );
  }

  return <ChangeList changes={changes} groupTitle="Fund Overview changes" />;
}

// ─── Portfolio Audit ─────────────────────────────────────────────────────────

function PortfolioAudit({
  fundId,
  period,
  previousPeriod,
  docMeta,
  docIds,
  threshold,
}: AuditProps & { threshold: number }) {
  const curDoc = pickDocForPeriod(docIds, period, docMeta);
  const prevDoc = previousPeriod
    ? pickDocForPeriod(docIds, previousPeriod, docMeta)
    : null;

  const queries = useQueries({
    queries: [
      {
        queryKey: ["audit", "port", fundId, curDoc],
        queryFn: () => api.getPortfolio(fundId, curDoc!),
        enabled: !!curDoc,
        staleTime: 30_000,
      },
      {
        queryKey: ["audit", "port", fundId, prevDoc],
        queryFn: () => api.getPortfolio(fundId, prevDoc!),
        enabled: !!prevDoc,
        staleTime: 30_000,
      },
    ],
  });

  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.find((q) => q.isError)?.error as Error | undefined;
  const curRows = (queries[0].data ?? []) as PeRow[];
  const prevRows = (queries[1].data ?? []) as PeRow[];

  const prevByInvId = useMemo(() => {
    const m = new Map<string, PeRow>();
    for (const r of prevRows) {
      const k = getPortfolioInvId(r);
      if (k) m.set(k, r);
    }
    return m;
  }, [prevRows]);

  const prevByCompany = useMemo(() => {
    const m = new Map<string, PeRow[]>();
    for (const r of prevRows) {
      const k = getPortfolioCompanyName(r);
      if (!k) continue;
      const existing = m.get(k) ?? [];
      existing.push(r);
      m.set(k, existing);
    }
    return m;
  }, [prevRows]);

  const curCompanyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of curRows) {
      const k = getPortfolioCompanyName(r);
      if (!k) continue;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return counts;
  }, [curRows]);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label="Analysing…" color="primary" />
      </div>
    );
  }

  if (!curDoc) {
    return (
      <AuditEmptyState message="No Portfolio document available for this period." />
    );
  }

  if (error) {
    return (
      <AuditEmptyState
        message={`Could not load Portfolio audit data: ${error.message}`}
      />
    );
  }

  if (curRows.length === 0) {
    return (
      <AuditEmptyState message="This portfolio document has no positions." />
    );
  }

  // Per-company blocks
  const blocks = curRows
    .map((row, rowIndex) => {
      const name = getPortfolioCompanyName(row) || "Unknown company";
      const invId = getPortfolioInvId(row);

      let prev = invId ? (prevByInvId.get(invId) ?? null) : null;
      if (!prev) {
        const prevCandidates = prevByCompany.get(name) ?? [];
        const isFallbackUnambiguous =
          name &&
          (curCompanyCounts.get(name) ?? 0) === 1 &&
          prevCandidates.length === 1;
        if (isFallbackUnambiguous) {
          prev = prevCandidates[0];
        }
      }

      const genInfoChanges = prev
        ? diffFillState(prev, row, PORTFOLIO_GENINFO_FIELDS)
        : [];

      const numericChanges = prev
        ? PORTFOLIO_NUMERIC_FIELDS.flatMap((f) => {
            const n = diffPeriodOverPeriodSingle(
              prev,
              row,
              f.key,
              f.label,
              threshold,
            );
            return n ? [n] : [];
          })
        : [];

      const emptyFlags = flagEmpty(row, PORTFOLIO_CRITICAL_FIELDS);

      return {
        key: invId || `${name}::${rowIndex}`,
        name,
        genInfoChanges,
        numericChanges,
        emptyFlags,
      };
    })
    .filter(
      (b) =>
        b.genInfoChanges.length > 0 ||
        b.numericChanges.length > 0 ||
        b.emptyFlags.length > 0,
    );

  if (blocks.length === 0) {
    return (
      <AuditEmptyState message="No issues detected — all positions look stable." />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!prevDoc && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          No previous period available — only showing empty-value flags.
        </div>
      )}
      {blocks.map((b) => (
        <div key={b.key} className="flex flex-col gap-2">
          <h3 className="text-sm font-bold text-gray-800 pl-1">{b.name}</h3>
          <EmptyFlagList
            flags={b.emptyFlags}
            groupTitle="Missing critical values"
          />
          <ChangeList changes={b.numericChanges} groupTitle="Numeric jumps" />
          <ChangeList
            changes={b.genInfoChanges}
            groupTitle="General info changes"
          />
        </div>
      ))}
    </div>
  );
}

// Helper for single-key period-over-period on PeRow
function diffPeriodOverPeriodSingle(
  before: PeRow,
  after: PeRow,
  key: string,
  label: string,
  threshold: number,
) {
  return compareNumericField(
    key,
    label,
    before[`${key}_val`] ?? before[`${key}_a`] ?? before[key] ?? "",
    after[`${key}_val`] ?? after[`${key}_a`] ?? after[key] ?? "",
    threshold,
  );
}

// ─── Capital Account Audit ───────────────────────────────────────────────────

function CapitalAccountAudit({
  fundId,
  period,
  previousPeriod,
  docMeta,
  docIds,
  threshold,
}: AuditProps & { threshold: number }) {
  // All docs for the selected period (possibly multiple sources)
  const byPeriod = groupByPeriod(docIds, docMeta);
  const curDocs = sortDocIdsByPeriodDesc(
    byPeriod.get(periodKey(period)) ?? [],
    docMeta,
  );
  const prevDocs = previousPeriod
    ? sortDocIdsByPeriodDesc(
        byPeriod.get(periodKey(previousPeriod)) ?? [],
        docMeta,
      )
    : [];

  // Fetch all relevant CA rows (one query per doc)
  const allDocs = [...curDocs, ...prevDocs];
  const queries = useQueries({
    queries: allDocs.map((docId) => ({
      queryKey: ["audit", "ca", fundId, docId],
      queryFn: () => api.getCapitalAccount(fundId, docId),
      enabled: !!docId,
      staleTime: 30_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.find((q) => q.isError)?.error as Error | undefined;

  const rowsByDoc = new Map<string, PeRow | null>();
  allDocs.forEach((docId, i) => {
    rowsByDoc.set(docId, (queries[i]?.data?.[0] ?? null) as PeRow | null);
  });

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label="Analysing…" color="primary" />
      </div>
    );
  }

  if (curDocs.length === 0) {
    return (
      <AuditEmptyState message="No Capital Account document available for this period." />
    );
  }

  if (error) {
    return (
      <AuditEmptyState
        message={`Could not load Capital Account audit data: ${error.message}`}
      />
    );
  }

  // 1. Cross-source diff (same period, multiple docs)
  const docLabel = (docId: string) => {
    const m = docMeta[docId];
    const type = m?.doc_type?.trim();
    const rp = m?.reporting_period ?? docId;
    return type ? `${type} · ${rp}` : rp;
  };

  const crossSourceDiffs =
    curDocs.length > 1
      ? diffCrossSource(
          curDocs.map((d) => ({
            docId: d,
            docLabel: docLabel(d),
            row: rowsByDoc.get(d) ?? null,
          })),
          threshold,
        )
      : [];

  // 2. Period-over-period delta — compare primary doc of each period
  const curRow = rowsByDoc.get(curDocs[0]) ?? null;
  const prevRow = prevDocs[0] ? (rowsByDoc.get(prevDocs[0]) ?? null) : null;
  const periodChanges = diffPeriodOverPeriod(prevRow, curRow, threshold);

  if (crossSourceDiffs.length === 0 && periodChanges.length === 0) {
    return <AuditEmptyState message="No significant differences detected." />;
  }

  return (
    <div className="flex flex-col gap-4">
      {curDocs.length > 1 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-[11px] text-gray-500">
            <span className="font-bold uppercase tracking-wider text-gray-500">
              Sources
            </span>{" "}
            · {curDocs.length} documents for {period}
          </div>
          <ul className="px-4 py-2 flex flex-wrap gap-2">
            {curDocs.map((d) => (
              <li
                key={d}
                className="text-[11px] rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600"
              >
                {docLabel(d)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <SourceDiffList
        diffs={crossSourceDiffs}
        groupTitle={`Cross-source discrepancies — ${period}`}
      />

      <ChangeList
        changes={periodChanges}
        groupTitle={
          previousPeriod
            ? `Period change · ${previousPeriod} → ${period}`
            : "Period change"
        }
      />

      {!previousPeriod && periodChanges.length === 0 && (
        <div className="text-xs text-gray-400 italic px-1">
          No previous period to compare against.
        </div>
      )}
    </div>
  );
}
